import { FieldValues, useFormContext } from "react-hook-form";
import { Content } from "./Content";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { StepProps, StepType } from "./types";
import { cva, type VariantProps } from "class-variance-authority";

const stepStyles = {
  container: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-[42px]",
        dialog: "gap-4",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
  contentContainer: cva("flex flex-col", {
    variants: {
      size: {
        desktop: "gap-[42px]",
        dialog: "gap-[32px]",
      },
    },
    defaultVariants: {
      size: "desktop",
    },
  }),
};

export type StepStyleProps = VariantProps<typeof stepStyles.container>;

export const Step = <T extends FieldValues>({
  step,
  stepNumber,
  totalSteps,
  onBack,
  onNext,
  onSkip,
  isSkippable = false,
  isNextButtonLoading,
  onItemHover,
  onStepOptionSelect,
  size = "desktop",
  isPreview = false,
  onExit,
}: StepProps<T>) => {
  const { id, title, description } = step;
  const form = useFormContext();
  const { dirtyFields } = form.formState;
  const { isDirty, invalid } = form.getFieldState(id, form.formState);
  const stepIds = Object.keys(form.getValues());
  const isLastStep = stepNumber === totalSteps;
  const isSubmit =
    stepIds.every((stepId) => !!dirtyFields[stepId]) && isLastStep;
  const isCustomStep = step.type === StepType.CUSTOM_STEP;

  return (
    <div
      className={`${stepStyles.container({
        size,
      })} phone:h-full phone:justify-between`}
    >
      <div className={stepStyles.contentContainer({ size })}>
        <Header
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          description={description}
          size={size}
        />
        <Content
          step={step}
          onStepOptionSelect={onStepOptionSelect}
          onItemHover={onItemHover}
          size={size}
        />
      </div>
      <Footer
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        onExit={onExit}
        isSkippable={isSkippable}
        isBackDisabled={stepNumber === 1}
        isSubmit={isSubmit}
        isNextDisabled={(!isDirty || invalid) && !isCustomStep}
        isNextButtonLoading={isNextButtonLoading}
        size={size}
        isPreview={isPreview}
      />
    </div>
  );
};
